interface HelpTooltip {
  readonly trigger: HTMLButtonElement;
  readonly content: HTMLSpanElement;
}

let nextTooltipId = 0;

export class HelpTooltips {
  private active: HelpTooltip | null = null;
  private pinned = false;
  private closeTimer: ReturnType<typeof setTimeout> | undefined;
  private openEvents: AbortController | null = null;

  public create(label: string, message: string): HTMLSpanElement {
    const container = document.createElement("span");
    container.className = "help-tooltip";
    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "help-tooltip__trigger";
    trigger.textContent = "?";
    trigger.setAttribute("aria-label", label);
    trigger.setAttribute("aria-expanded", "false");
    const content = document.createElement("span");
    content.className = "help-tooltip__content";
    content.id = `help-tooltip-${++nextTooltipId}`;
    content.popover = "auto";
    content.setAttribute("role", "tooltip");
    content.textContent = message;
    trigger.popoverTargetElement = content;
    trigger.setAttribute("aria-describedby", content.id);
    container.append(trigger, content);
    const tooltip = { trigger, content };

    container.addEventListener("pointerenter", (event) => {
      if (event.pointerType === "mouse") this.open(tooltip);
    });
    container.addEventListener("pointerleave", (event) => {
      if (
        event.pointerType !== "mouse" ||
        this.active !== tooltip ||
        this.pinned ||
        container.contains(document.activeElement)
      )
        return;
      this.cancelClose();
      // Allow the pointer to cross the gap between the question mark and help text.
      this.closeTimer = setTimeout(() => this.close(), 150);
    });
    trigger.addEventListener("focus", () => this.open(tooltip));
    container.addEventListener("focusout", (event) => {
      if (
        this.active === tooltip &&
        !this.pinned &&
        (!(event.relatedTarget instanceof Node) || !container.contains(event.relatedTarget))
      ) {
        this.close();
      }
    });
    trigger.addEventListener("click", (event) => {
      // A click pins help that was already opened by hover or focus.
      event.preventDefault();
      if (this.active === tooltip && this.pinned) {
        this.close();
      } else {
        this.open(tooltip);
        this.pinned = true;
      }
    });
    content.addEventListener("beforetoggle", (event) => {
      if (event.newState === "closed" && this.active === tooltip) this.reset();
    });
    return container;
  }

  public close(): void {
    const tooltip = this.active;
    this.reset();
    tooltip?.content.hidePopover();
  }

  private open(tooltip: HelpTooltip): void {
    this.cancelClose();
    if (this.active === tooltip) return;
    this.close();
    this.active = tooltip;
    tooltip.content.showPopover({ source: tooltip.trigger });
    tooltip.trigger.setAttribute("aria-expanded", "true");
    this.position(tooltip);
    this.openEvents = new AbortController();
    const options = { capture: true, signal: this.openEvents.signal };
    window.addEventListener(
      "scroll",
      (event) => {
        if (!(event.target instanceof Node) || !tooltip.content.contains(event.target)) {
          this.close();
        }
      },
      options,
    );
    window.addEventListener("resize", () => this.close(), options);
  }

  private position({ trigger, content }: HelpTooltip): void {
    const anchor = trigger.getBoundingClientRect();
    const popup = content.getBoundingClientRect();
    const gap = 8;
    const left = Math.max(
      gap,
      Math.min(anchor.right - popup.width, window.innerWidth - popup.width - gap),
    );
    const below = anchor.bottom + gap;
    const top =
      below + popup.height <= window.innerHeight - gap
        ? below
        : Math.max(gap, anchor.top - popup.height - gap);
    content.style.left = `${left}px`;
    content.style.top = `${top}px`;
  }

  private cancelClose(): void {
    clearTimeout(this.closeTimer);
    this.closeTimer = undefined;
  }

  private reset(): void {
    this.cancelClose();
    this.active?.trigger.setAttribute("aria-expanded", "false");
    this.active = null;
    this.pinned = false;
    this.openEvents?.abort();
    this.openEvents = null;
  }
}
